<script setup>


import notification from "@/utils/plugins/NotificationService";
import { useUiStore } from "@/app/stores/uiStore";
import { computed, ref, onMounted, watch } from 'vue';
import { Upload } from '@element-plus/icons-vue';

// Services
import FuelReceiptMediaObjectService from '@/api/services/fuelReceiptMediaObject.service';

const baseUrl = ref(process.env.VUE_APP_BASE_URI);

const cost = ref(0)
const fuelReceiptMediaObjects = ref([]);

const file = ref(null);
const selectedFile = ref(null);
const selectedFileName = ref("Ningún archivo seleccionado");
const fileStatus = ref(null);

const fileList = ref([])

const isImageModalVisible = ref(false);
const modalImageUrl = ref(null);

// eslint-disable-next-line no-undef
const props = defineProps({
    isReturning: { type: Boolean, default: false },
    request: Object,
    canEdit: { type:Boolean, default: false}
})

// eslint-disable-next-line no-undef
const emit = defineEmits(['addFile', 'removeFile', 'update:selectedFile'])

const uiStore = useUiStore();

const request = computed(() => {
    if (props.request) return props.request
    return null;
})

const syncFileList = () => {
    const files = request.value?.fuelReceiptMediaObjects || [];
    fileList.value = [...files];
    fuelReceiptMediaObjects.value = files.map((item) => item['@id']).filter(Boolean);
}

onMounted(() => {
    syncFileList();
})

watch(request, () => {
    syncFileList();
})

// Tickets (Adjuntar archivos y costes)
// Upload Tickets
const handleFileUpload = () => {
    const allowedExtensions = ['pdf', 'zip', 'rar', 'jpg', 'jpeg', 'png', 'doc', 'docx'];

    const input = file.value; // <input>
    const newFile = input?.files?.[0]; // File
    selectedFileName.value = newFile ? newFile.name : "Ningún archivo seleccionado";


    if (!newFile) {
        selectedFile.value = null;
        return;
    }

    const extension = newFile.name.split('.').pop().toLowerCase();

    if (newFile.size > 5242880) {
        notification.addNotification({
            type: 'error',
            message: 'Tamaño máximo permitido 5 MB.',
            duration: 6000
        });
        resetInput();
        return;
    }

    if (!allowedExtensions.includes(extension)) {
        notification.addNotification({
            type: 'error',
            message: 'Este formato no está permitido.',
            duration: 6000
        });
        resetInput();
        return;
    }
    selectedFile.value = newFile;
    emit('update:selectedFile', newFile);
    fileStatus.value = null;
}

const resetInput = () => {
    selectedFile.value = null;
    emit('update:selectedFile', null);
    fileStatus.value = 'error';
    if (file.value) {
        file.value.value = '';
    }
};

const uploadTicket = async () => {
    if (cost.value <= 0) {
        notification.addNotification({
            type: 'error',
            message: 'El coste del ticket tiene que ser mayor que 0',
            duration: 6000
        });
        return;
    }
    uiStore.startLoading();
    await FuelReceiptMediaObjectService.uploadDocumentFile(selectedFile.value, cost.value, request.value['@id'])
        .then((res) => {
            fileList.value = [...fileList.value, res.data];
            fuelReceiptMediaObjects.value = fileList.value.map((item) => item['@id']).filter(Boolean);
            emit('addFile', fuelReceiptMediaObjects.value)
            notification.addNotification({
                type: 'success',
                message: 'Documento cargado correctamente',
                duration: 6000
            });
            resetInput();
            cost.value = 1;
            uiStore.stopLoading();
        })
        .catch(() => {
            notification.addNotification({
                type: 'error',
                message: 'Error al cargar el documento',
                duration: 6000
            });
            uiStore.stopLoading();
        });
}

// Delete tickets
const removeAllTickets = async () => {
    let length = fileList.value.length
    uiStore.startLoading();
    try {
        await Promise.all(fileList.value.map(tick =>
            FuelReceiptMediaObjectService.deleteFuelReceiptMediaObject(tick.id)
        ))
        if (length) {
            notification.addNotification({
                type: 'success',
                message: length > 1 ? 'Tickets eliminado correctamente' : 'Ticket eliminados correctamente',
                duration: 6000
            });
        }
        fileList.value = [];
        fuelReceiptMediaObjects.value = [];
        emit('removeFile', fuelReceiptMediaObjects.value)
    } catch {
        notification.addNotification({
            type: 'error',
            message: 'Error al intentar eliminar el ticket seleccionado.',
            duration: 6000
        });
    } finally {
        uiStore.stopLoading()
    }
}

defineExpose({ removeAllTickets })

const deleteFuelReceipt = async (document) => {
    uiStore.startLoading();
    try {
        await FuelReceiptMediaObjectService.deleteFuelReceiptMediaObject(document.id)
        const deletedIri = document['@id'];
        fileList.value = fileList.value.filter((item) => {
            if (deletedIri && item['@id']) return item['@id'] !== deletedIri;
            return item.id !== document.id;
        });
        fuelReceiptMediaObjects.value = fileList.value.map((item) => item['@id']).filter(Boolean);
        emit('removeFile', fuelReceiptMediaObjects.value)
        notification.addNotification({
            type: 'success',
            message: 'Ticket eliminado correctamente',
            duration: 6000
        });
    } catch {
        notification.addNotification({
            type: 'error',
            message: 'Error al intentar eliminar el ticket seleccionado.',
            duration: 6000
        });
    } finally {
        uiStore.stopLoading();
    }
}

//Modal img
const openImageModal = (url) => {
    modalImageUrl.value = baseUrl.value + url;
    isImageModalVisible.value = true;
};

const handleCloseModal = () => {
    isImageModalVisible.value = false;
    modalImageUrl.value = null;
};

watch(modalImageUrl, (newUrl) => {
    if (newUrl) {
        uiStore.startLoading();
    }
});

const onImageLoad = () => {
    uiStore.stopLoading();
};

const onImageError = () => {
    uiStore.stopLoading();
    console.warn("Error cargando el ticket");
};

</script>

<template>
    <div>
        <div v-if="request != null || canEdit">
            <!-- Listado tickets -->
            <div v-if="fileList && fileList.length > 0">
                <div class="col-md-8 fuel-receipt-list">
                    <div class="row tex-center fuel-receipt-list-row">
                        <div class="col-md-3 d-inline-flex">
                            <label class="label-absence">Tickets</label>
                        </div>
                        <div class="col-md-9">
                            <div class="table-documents">
                                <table class="styled-table">
                                    <tbody>
                                        <tr v-for="(document, index) in fileList" :key="document['@id'] || document.id">
                                            <td class="pointer document-name"
                                                @click="openImageModal(document.contentUrl)">
                                                <i class="fas fa-file-image icon-file"></i>
                                                {{ document.name || 'Ticket ' + (index + 1) }}
                                            </td>
                                            <td class="cost-cell pointer" @click="openImageModal(document.contentUrl)">
                                                <span>{{ document.cost }} €</span>
                                            </td>
                                            <td v-if="isReturning || canEdit">
                                                <div class="buttons d-inline-flex">
                                                    <div class="pointer">
                                                        <font-awesome-icon class="btn_icon delete_btn" icon="times"
                                                            title="Eliminar documento" style="margin-left: 5px"
                                                            @click="deleteFuelReceipt(document)" />
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Añadir tickets -->
            <div class="col-12 row d-flex fuel-upload-row" v-if="isReturning || canEdit">
                <!-- <div class="col-md-12 mt-2 justify-content-center d-flex">
                    <el-button class="btn btn_yellow" style="max-width: 200px;" @click="uploadTicket"
                        :disabled="selectedFile === null || cost <= 0">
                        AÑADIR TICKET
                    </el-button>
                </div> -->
                <div class="col-md-8 el-col-xs-24 mt-2">
                    <label class="w-100" for="file">Adjuntar tickets:</label>
                    <div class="col-md-12 form-item text-left">
                        <!-- <input class="custom-file" id="file" ref="file" type="file" @change.stop="handleFileUpload"> -->
                        <div class="file-upload-wrapper">
                            <label for="file" class="btn-upload">
                                <i class="fas fa-image"></i> Seleccionar ticket
                            </label>
                            <span class="file-name">{{ selectedFileName }}</span>
                            <input id="file" ref="file" type="file" @change.stop="handleFileUpload"
                                style="display:none;" />
                        </div>
                    </div>
                </div>
                <div class="col-md-3 el-col-xs-24 mt-2">
                    <label class="w-100" for="cost">Coste del ticket:</label>
                    <el-input-number v-model="cost" :step="1.00" :precision="2" :min="0" id="cost"
                        placeholder="Introduzca el coste" />
                </div>
                <div class="col-md-1 el-col-xs-24 fuel-upload-button-col">
                    <button class="btn btn_yellow align-self-center justify-content-end btn-plus-add-ticket"
                        :disabled="selectedFile === null || cost < 1" @click="uploadTicket" title="Añadir Ticket">
                        <el-icon>
                            <Upload />
                        </el-icon>
                    </button>
                </div>
            </div>
        </div>

        <!-- Modal Ticket Img -->
        <el-dialog v-model="isImageModalVisible" width="60%" :before-close="handleCloseModal">
            <img v-if="modalImageUrl" :src="modalImageUrl" style="width: 100%; max-height: 80vh; object-fit: contain;"
                @load="onImageLoad" @error="onImageError" alt="Ticket" />
        </el-dialog>
    </div>
</template>

<style scoped lang="scss">
/* Tabla tickets */
.styled-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
    font-family: Arial, sans-serif;
}

.styled-table {
    td {
        padding: 8px 12px;
        border-bottom: 1px solid #eee;
        vertical-align: middle;
    }

    span,
    td {
        font-size: 22px;
    }
}

.document-name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    color: #007bff;
}

.document-name:hover {
    text-decoration: underline;
}

.icon-file {
    font-size: 1.1rem;
    color: #007bff;
}

.cost-cell {
    font-weight: 600;
    color: #2a2a2a;
}

.actions-cell {
    text-align: center;
}

.btn-upload {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #007bff;
    color: white;
    padding: 8px 14px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    transition: background-color .2s;
}

.btn-upload:hover {
    background-color: #0056b3;
}

.btn-upload i {
    font-size: 18px;
}

.file-name {
    font-style: italic;
    color: #555;
}



.btn-plus-add-ticket {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
    padding: 0;
    margin-top: 1.75em;
}

.btn[disabled] {
    :deep(.el-icon) {
        color: #999 !important;
    }
}

@media (max-width: 767px) {
    .fuel-receipt-list {
        width: 100%;
    }

    .fuel-receipt-list-row {
        row-gap: 8px;
    }

    .table-documents {
        overflow-x: auto;
    }

    .styled-table td,
    .styled-table span {
        font-size: 16px;
    }

    .fuel-upload-row {
        row-gap: 12px;
    }

    .file-upload-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .fuel-upload-button-col {
        display: flex;
        justify-content: flex-end;
    }

    .btn-plus-add-ticket {
        margin-top: 0;
    }

    :deep(.el-input-number) {
        width: 100%;
    }
}
</style>
